import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ConsoleTable from '../../components/ConsoleTable.jsx';
import { api } from '../../lib/api.js';
import { buildSendLogRows } from '../../lib/send-log-rows.js';
import { AUDIENCE_LABEL, formatWhen } from '../../lib/watch-format.js';

const SOURCE_NAME = { daily: 'Daily collection', live: 'Live watch' };

// Admin-only: the portal refuses the Watch send log to every other role, and the section does not route operators here.
export default function NotificationsSendLog({ customerId, data }) {
  const [liveItems, setLiveItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const reqRef = useRef(0);
  const moreBusyRef = useRef(false);

  const load = useCallback(async (after) => {
    if (after && moreBusyRef.current) return; // guard double submit of Load more
    const req = ++reqRef.current;
    moreBusyRef.current = Boolean(after);
    if (after) setLoadingMore(true); else setLoading(true);
    setError(null);
    try {
      const r = await api.listWatchSendLog(customerId, after ? { cursor: after } : {});
      if (reqRef.current !== req) return;
      setLiveItems((prev) => (after ? [...prev, ...(r.items ?? [])] : (r.items ?? [])));
      setCursor(r.cursor ?? null);
    } catch (e) {
      if (reqRef.current !== req) return;
      setError(e?.message || 'The send log could not be loaded.');
    } finally {
      if (after) moreBusyRef.current = false;
      if (reqRef.current === req) { setLoading(false); setLoadingMore(false); }
    }
  }, [customerId]);

  useEffect(() => {
    setLiveItems([]);
    setCursor(null);
    load(null);
    return () => { reqRef.current += 1; };
  }, [load]);

  const ruleLabel = (id) => data.classic?.catalog?.find((e) => e.id === id)?.label ?? id;
  const rows = useMemo(
    () => buildSendLogRows({ liveItems, history: data.history?.history ?? [], catalog: data.watch?.catalog ?? null, ruleLabel }),
    [liveItems, data.history, data.watch, data.classic], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const columns = [
    { id: 'when', header: 'Time', sortValue: (r) => Date.parse(r.when) || 0, cell: (r) => <span className="whitespace-nowrap">{formatWhen(r.when)}</span> },
    { id: 'source', header: 'Source', hideBelow: 'md', cell: (r) => SOURCE_NAME[r.source] },
    {
      id: 'recipient',
      header: 'Recipient',
      sortValue: (r) => r.recipient.toLowerCase(),
      cell: (r) => (<div className="min-w-0 break-words"><div>{r.recipient}</div>{r.address ? <div className="text-xs text-slate-600">{r.address}</div> : null}</div>),
    },
    { id: 'event', header: 'Event', sortValue: (r) => r.event.toLowerCase(), cell: (r) => r.event },
    { id: 'cadence', header: 'Cadence', hideBelow: 'lg', cell: (r) => r.cadence },
    { id: 'status', header: 'Status', sortValue: (r) => r.status, cell: (r) => r.status },
  ];

  const filters = [
    { id: 'source', label: 'Source', options: [{ value: 'daily', label: 'Daily collection' }, { value: 'live', label: 'Live watch' }], test: (r, v) => r.source === v },
    {
      id: 'status',
      label: 'Status',
      options: [{ value: 'delivered', label: 'Delivered or sent' }, { value: 'held', label: 'Held' }, { value: 'failed', label: 'Failed or bounced' }, { value: 'suppressed', label: 'Suppressed' }],
      test: (r, v) => r.bucket === v,
    },
    { id: 'audience', label: 'Audience', options: Object.entries(AUDIENCE_LABEL).map(([value, label]) => ({ value, label })), test: (r, v) => r.audiences.includes(v) },
  ];

  return (
    <ConsoleTable
      label="Send log"
      columns={columns}
      rows={rows}
      getRowId={(r) => r.id}
      rowName={(r) => r.recipient}
      searchText={(r) => `${r.recipient} ${r.address ?? ''} ${r.event} ${r.status}`}
      searchPlaceholder="Search the send log"
      filters={filters}
      defaultSort={{ columnId: 'when', direction: 'descending' }}
      renderExpanded={(r) => (
        <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-1 text-sm">
          <dt className="text-slate-600">Audience</dt>
          <dd>{r.audiences.map((a) => AUDIENCE_LABEL[a] ?? a).join(', ') || 'Not applicable'}</dd>
          <dt className="text-slate-600">Answer</dt>
          <dd>{r.answer ?? 'No answer'}</dd>
        </dl>
      )}
      loading={loading}
      error={error && rows.length === 0 ? error : null}
      onRetry={() => load(null)}
      hasMore={Boolean(cursor)}
      onLoadMore={() => load(cursor)}
      loadingMore={loadingMore}
      caption={error && rows.length > 0 ? <span role="alert" className="text-red-700">{error}</span> : 'One page of Live watch sends at a time; Load more fetches the next. The filters apply to what is loaded.'}
      emptyState={{ title: 'Nothing sent yet', body: 'Sent emails appear here.' }}
    />
  );
}
